// Generic DOKI OCR service for processing various document types
import axios from 'axios';

/**
 * Generic DOKI service for processing documents
 * Uses the DOKI OCR service with Bearer token authentication
 * This is a reusable service that can handle different document types
 */
class DokiService {
  constructor() {
    // Initialize DOKI API client
    this.client = axios.create({
      baseURL: 'https://thedoki-orchestrator-dev.bysur.com',
      timeout: 120000, // 120 second timeout for document processing
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_DOKI_KEY}`,
        // Don't set Content-Type - let axios handle multipart form boundary automatically
      },
    });

    // Add request interceptor for debugging
    this.client.interceptors.request.use(
      (config) => {
        console.log('DOKI Request:', {
          url: config.url,
          method: config.method,
          hasAuth: config.headers.Authorization ? 'Yes' : 'No',
          timeout: config.timeout,
        });
        return config;
      },
      (error) => {
        console.error('DOKI Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        console.log('DOKI Response Success:', {
          status: response.status,
          dataType: typeof response.data,
          hasData: !!response.data,
        });
        return response;
      },
      (error) => {
        console.error('DOKI Response Error:', {
          status: error.response?.status,
          message: error.message,
          data: error.response?.data,
        });
        return Promise.reject(error);
      }
    );
  }

  /**
   * Process a document using DOKI OCR
   * @param {File} file - The document file to process (PDF, JPG, PNG)
   * @param {Object} options - Processing options
   * @param {string} options.projectId - The DOKI project ID for this document type
   * @param {string} options.formDataKey - The form data key for the file upload
   * @param {string} options.documentType - The document type for logging (e.g., 'CPR', 'Passport', 'Work Permit')
   * @returns {Promise<Object>} Document processing result
   */
  async processDocument(file, options) {
    const { projectId, formDataKey, documentType = 'Document' } = options;

    try {
      // Validate input
      if (!file || !(file instanceof File)) {
        throw new Error('Invalid file provided');
      }

      if (!projectId) {
        throw new Error('Project ID is required');
      }

      if (!formDataKey) {
        throw new Error('Form data key is required');
      }

      // Validate environment variables
      const apiKey = process.env.REACT_APP_DOKI_KEY;

      if (!apiKey) {
        throw new Error('REACT_APP_DOKI_KEY environment variable is not configured');
      }

      // Create FormData for multipart request
      const formData = new FormData();
      formData.append(formDataKey, file, file.name);

      // Log file details for debugging
      console.log(`Processing ${documentType} document:`, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        formDataKey,
        projectId,
      });

      // Make DOKI request
      const response = await this.client.post(`/api/project/${projectId}/run`, formData);

      // Validate response structure
      if (!response.data) {
        throw new Error(`No data received from ${documentType} service`);
      }

      // Handle array response format
      const documentData = Array.isArray(response.data) ? response.data[0] : response.data;

      if (!documentData || !documentData.output) {
        throw new Error(`Invalid ${documentType} response format`);
      }

      return {
        success: true,
        data: documentData,
        rawResponse: response.data,
      };
    } catch (error) {
      // Categorize errors for better handling
      let errorType = 'UNKNOWN_ERROR';
      let errorMessage = error.message;

      if (error.code === 'ECONNABORTED') {
        errorType = 'TIMEOUT_ERROR';
        errorMessage = `${documentType} processing timed out`;
      } else if (error.response?.status === 401) {
        errorType = 'AUTH_ERROR';
        errorMessage = 'Authentication failed';
      } else if (error.response?.status === 400) {
        errorType = 'BAD_REQUEST';
        errorMessage = 'Invalid file or request format';
      } else if (error.response?.status >= 500) {
        errorType = 'SERVER_ERROR';
        errorMessage = `${documentType} service is temporarily unavailable`;
      } else if (error.message.includes('environment variable')) {
        errorType = 'CONFIG_ERROR';
        errorMessage = error.message;
      }

      console.error(`${documentType} Processing Error:`, {
        type: errorType,
        message: errorMessage,
        originalError: error.message,
        status: error.response?.status,
      });

      return {
        success: false,
        error: {
          type: errorType,
          message: errorMessage,
          details: error.response?.data || error.message,
        },
      };
    }
  }
}

// Create singleton instance
const dokiService = new DokiService();

// Export the service instance and main method
export const processDocument = (file, options) => dokiService.processDocument(file, options);
export default dokiService;
